import { Button } from '@avanta/shared/components/ui/Button';
import { Label } from '@avanta/shared/components/ui/Label';
import { Textarea } from '@avanta/shared/components/ui/Textarea';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SURVEY_TEMPLATES } from 'constants/surveys';
import { useSurveyContext } from 'contexts/Survey';
import type { OptionDTO } from 'types/surveys';
import { type QuestionCardProps, type QuestionForm } from './types';

const TEMPLATE_STYLES = {
  [SURVEY_TEMPLATES.VERTICAL_OPTIONS]: {
    container: 'flex-col items-center',
    optionWrapper: 'max-w-120',
  },
  [SURVEY_TEMPLATES.HORIZONTAL_OPTIONS]: {
    container: 'max-am-lg:flex-col',
  },
  [SURVEY_TEMPLATES.RADIO_BUTTONS]: {
    container: 'justify-center max-am-md:justify-between gap-2',
    optionWrapper: 'w-fit',
    label: 'min-w-11 w-fit px-4 py-2',
  },
  [SURVEY_TEMPLATES.TEXT_AREA]: {
    container: 'flex flex-col gap-2 items-center',
  },
} as const;

export const QuestionCard = ({ saveButton }: QuestionCardProps): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const { question, isPending, saveResponse } = useSurveyContext();
  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<QuestionForm>({
    mode: 'onChange',
  });

  if (!question) return null;

  const onSubmit = (data: QuestionForm): void => {
    const options = data[question.id] ?? [];
    const textValue = data[`${question.id}-text`];
    const requiredTextValue = selectedOption?.requiresText ? textValue?.toString() : undefined;
    const optionIds = Array.isArray(options) ? options : [options];

    saveResponse(question.id, optionIds, requiredTextValue);
  };

  const inputType = question.isMultipleOption ? 'checkbox' : 'radio';
  const template = SURVEY_TEMPLATES[question.template];
  const styles = TEMPLATE_STYLES[template];
  const isRadioButtons = template === SURVEY_TEMPLATES.RADIO_BUTTONS;
  const selectedOption = question.optionsDTO.find(option => option.id === watch(question.id));

  return (
    <form className="flex flex-col gap-6" key={question.id} onSubmit={handleSubmit(onSubmit)}>
      {isRadioButtons && (
        <div className="flex items-center justify-between am-md:hidden">
          <span className="text-xs text-center">{t('WellBeing.Test.options.veryRare')}</span>
          <span className="text-xs text-center">{t('WellBeing.Test.options.almostAlways')}</span>
        </div>
      )}
      <div className={cn('flex gap-4 items-center', styles.container)}>
        {isRadioButtons && (
          <span className="text-xs text-center max-am-md:hidden">
            {t('WellBeing.Test.options.veryRare')}
          </span>
        )}
        {question.optionsDTO.map((option: OptionDTO) => (
          <div
            key={option.id}
            className={cn('w-full', 'optionWrapper' in styles && styles.optionWrapper)}
          >
            <input
              type={inputType}
              {...register(question.id, { required: true })}
              id={option.id}
              value={option.id}
              className="sr-only peer"
            />
            <label
              htmlFor={option.id}
              className={cn(
                'flex items-center justify-center gap-2 px-6 py-4 rounded-xl border transition-all peer-checked:bg-blue-100 peer-checked:hover:bg-blue-100 hover:bg-gray-100 w-full',
                'label' in styles && styles.label
              )}
            >
              {option.emoji && <span>{option.emoji}</span>}
              <span className="text-sm text-center">{option.text}</span>
            </label>
          </div>
        ))}
        {isRadioButtons && (
          <span className="text-xs text-center max-am-md:hidden">
            {t('WellBeing.Test.options.almostAlways')}
          </span>
        )}
      </div>
      {selectedOption?.requiresText && (
        <div className="flex flex-col gap-2 items-center">
          <div className="max-w-120 w-full">
            <Label className="text-start block mb-2" htmlFor={`${question.id}-text`}>
              {t('WellBeing.Test.other.placeholder')}
            </Label>
            <Textarea
              id={`${question.id}-text`}
              className="w-full"
              {...register(`${question.id}-text`)}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col items-center gap-2">
        <Button
          size="lg"
          disabled={!isValid}
          isLoading={isPending}
          type="submit"
          className="w-fit min-w-[120px] mt-2"
        >
          {t(saveButton)}
        </Button>
      </div>
    </form>
  );
};
